import React, { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { Upload, FileSpreadsheet, CheckCircle, X, ChevronRight, ChevronLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useApp } from '../context/AppContext';

// 우리 시스템의 필드 정의
const OUR_FIELDS = [
  { key: 'name',        label: '이름',         required: true,  group: 'member' },
  { key: 'phone',       label: '전화번호',      required: true,  group: 'member' },
  { key: 'gender',      label: '성별',          required: false, group: 'member' },
  { key: 'birthDate',   label: '생년월일',      required: false, group: 'member' },
  { key: 'memo',        label: '메모',          required: false, group: 'member' },
  { key: 'productName', label: '회원권 상품명', required: false, group: 'membership' },
  { key: 'startDate',   label: '회원권 시작일', required: false, group: 'membership' },
  { key: 'endDate',     label: '회원권 만료일', required: false, group: 'membership' },
];

// 컬럼 자동 매핑 키워드
const AUTO_MATCH_KEYWORDS = {
  name:        ['이름', '성명', '회원이름', '회원명', 'name'],
  phone:       ['전화', '연락처', '핸드폰', '휴대폰', '전화번호', 'phone', '휴대전화', '전화 번호'],
  gender:      ['성별', 'gender', '남녀'],
  birthDate:   ['생년월일', '생일', '출생일', 'birth', '생년', '생년 월일'],
  memo:        ['메모', '비고', '노트', 'memo', '특이사항', '참고'],
  productName: ['상품', '회원권', '상품명', '회원권명', '이용권', '회원권 상품'],
  startDate:   ['시작일', '등록일', '가입일', '시작', '회원권시작', '개시일'],
  endDate:     ['만료일', '종료일', '만기일', '만료', '만기', '회원권만료', '유효기간'],
};

// 전화번호 정규화: 숫자만 추출 후 010-XXXX-XXXX 형식
function normalizePhone(val) {
  if (!val) return '';
  const digits = String(val).replace(/[^0-9]/g, '');
  if (digits.length === 11) return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return digits;
}

// 날짜 정규화: 다양한 형식 → YYYY-MM-DD
function normalizeDate(val) {
  if (!val) return '';
  const s = String(val).trim();
  // 이미 YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // YYYY.MM.DD or YYYY/MM/DD
  const m1 = s.match(/^(\d{4})[./](\d{1,2})[./](\d{1,2})$/);
  if (m1) return `${m1[1]}-${m1[2].padStart(2, '0')}-${m1[3].padStart(2, '0')}`;
  // YYYYMMDD
  if (/^\d{8}$/.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6)}`;
  // 2024년 1월 15일
  const m2 = s.match(/(\d{4})년\s*(\d{1,2})월\s*(\d{1,2})일/);
  if (m2) return `${m2[1]}-${m2[2].padStart(2, '0')}-${m2[3].padStart(2, '0')}`;
  return '';
}

// 성별 정규화
function normalizeGender(val) {
  if (!val) return '';
  const s = String(val).trim().toLowerCase();
  if (['남', '남자', 'm', 'male', '1'].includes(s)) return '남';
  if (['여', '여자', 'f', 'female', '2'].includes(s)) return '여';
  return '';
}

// 컬럼 자동 매핑
function autoMatch(headers) {
  const mapping = {};
  OUR_FIELDS.forEach(field => {
    const keywords = AUTO_MATCH_KEYWORDS[field.key] || [];
    const found = headers.find(h =>
      keywords.some(kw => h.toLowerCase().includes(kw.toLowerCase()))
    );
    mapping[field.key] = found || '';
  });
  return mapping;
}

// 행 데이터 변환 (매핑 적용)
function transformRow(rawRow, mapping) {
  const row = {};
  OUR_FIELDS.forEach(field => {
    const col = mapping[field.key];
    const raw = col ? rawRow[col] : undefined;
    if (field.key === 'phone')     row.phone     = normalizePhone(raw);
    else if (field.key === 'birthDate' || field.key === 'startDate' || field.key === 'endDate')
                                   row[field.key] = normalizeDate(raw);
    else if (field.key === 'gender') row.gender   = normalizeGender(raw);
    else                           row[field.key] = raw ? String(raw).trim() : '';
  });
  return row;
}

// 행 유효성 검사
function validateRow(row) {
  const errors = [];
  if (!row.name)  errors.push('이름 없음');
  if (!row.phone) errors.push('전화번호 없음');
  else if (!/^\d{3}-\d{3,4}-\d{4}$/.test(row.phone)) errors.push('전화번호 형식 오류');
  return errors;
}

const CHUNK_SIZE = 100;

export default function ImportMembersModal({ isOpen, onClose }) {
  const { gymId, showToast } = useApp();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(1); // 1: 업로드, 2: 매핑, 3: 가져오기
  const [fileName, setFileName] = useState('');
  const [rawHeaders, setRawHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [isDragging, setIsDragging] = useState(false);

  // Step 3 상태
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null); // { memberSuccess, memberFailed, membershipSuccess }

  const reset = useCallback(() => {
    setStep(1);
    setFileName('');
    setRawHeaders([]);
    setRawRows([]);
    setMapping({});
    setImporting(false);
    setProgress(0);
    setResult(null);
  }, []);

  const handleClose = () => {
    reset();
    onClose();
  };

  // 파일 파싱
  const parseFile = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array', cellDates: false });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { raw: false, defval: '' });
        if (rows.length === 0) { showToast('데이터가 없습니다.', 'error'); return; }
        const headers = Object.keys(rows[0]);
        setRawHeaders(headers);
        setRawRows(rows);
        setMapping(autoMatch(headers));
        setFileName(file.name);
        setStep(2);
      } catch {
        showToast('파일을 읽을 수 없습니다. xlsx/xls/csv 파일인지 확인해주세요.', 'error');
      }
    };
    reader.readAsArrayBuffer(file);
  }, [showToast]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) parseFile(file);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) parseFile(file);
  };

  // 변환된 행 목록 (step 2, 3에서 공유)
  const transformedRows = rawRows.map(r => transformRow(r, mapping));
  const validRows = transformedRows.filter(r => validateRow(r).length === 0);
  const invalidRows = transformedRows.filter(r => validateRow(r).length > 0);
  const withMembership = validRows.filter(r => r.productName || r.endDate);

  // 가져오기 실행
  const handleImport = async () => {
    if (!gymId || validRows.length === 0) return;
    setImporting(true);
    setProgress(0);

    try {
      // 1) 회원 데이터 준비
      const memberRows = validRows.map(r => ({
        gym_id: gymId,
        name: r.name,
        phone: r.phone,
        gender: r.gender || null,
        birth_date: r.birthDate || null,
        memo: r.memo || null,
        status: 'active',
      }));

      // 2) 회원 일괄 삽입
      let insertedMembers = [];
      let memberFailed = 0;
      for (let i = 0; i < memberRows.length; i += CHUNK_SIZE) {
        const chunk = memberRows.slice(i, i + CHUNK_SIZE);
        const { data, error } = await supabase.from('members').insert(chunk).select('id, phone');
        if (error) {
          for (const row of chunk) {
            const { data: d2 } = await supabase.from('members').insert([row]).select('id, phone');
            if (d2?.[0]) insertedMembers.push(d2[0]);
            else memberFailed++;
          }
        } else if (data) {
          insertedMembers = [...insertedMembers, ...data];
        }
        setProgress(Math.round((Math.min(i + CHUNK_SIZE, memberRows.length) / memberRows.length) * 70));
      }

      // 3) 회원권 데이터 준비 (전화번호로 member_id 매핑)
      const phoneToId = {};
      insertedMembers.forEach(m => { phoneToId[m.phone] = m.id; });

      const membershipRows = validRows
        .filter(r => r.productName || r.endDate)
        .map(r => {
          const memberId = phoneToId[r.phone];
          if (!memberId) return null;
          const status = r.endDate && new Date(r.endDate) < new Date() ? 'expired' : 'active';
          return {
            gym_id: gymId,
            member_id: memberId,
            product_name: r.productName || null,
            start_date: r.startDate || null,
            end_date: r.endDate || null,
            status,
            freeze_history: [],
          };
        })
        .filter(Boolean);

      // 4) 회원권 일괄 삽입
      let membershipSuccess = 0;
      for (let i = 0; i < membershipRows.length; i += CHUNK_SIZE) {
        const chunk = membershipRows.slice(i, i + CHUNK_SIZE);
        const { data } = await supabase.from('memberships').insert(chunk).select('id');
        if (data) membershipSuccess += data.length;
        setProgress(70 + Math.round((Math.min(i + CHUNK_SIZE, membershipRows.length) / Math.max(membershipRows.length, 1)) * 30));
      }
      if (membershipRows.length === 0) setProgress(100);

      setResult({ memberSuccess: insertedMembers.length, memberFailed, membershipSuccess });
      showToast(`${insertedMembers.length}명 등록 완료!`);

      // AppContext 새로고침 (페이지 데이터 반영)
      // members/memberships 재로드는 reload() 또는 AppContext 직접 업데이트
      // 간단하게 페이지 새로고침 트리거
      if (typeof window !== 'undefined') window.location.reload();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setImporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* 헤더 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">회원 일괄 가져오기</h2>
            <p className="text-xs text-gray-400 mt-0.5">엑셀(.xlsx) 또는 CSV 파일로 회원을 한 번에 등록합니다</p>
          </div>
          <button onClick={handleClose} className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 단계 표시 */}
        <div className="flex items-center gap-2 px-6 py-3 border-b border-gray-100 dark:border-gray-700">
          {['파일 업로드', '컬럼 매핑', '가져오기'].map((label, idx) => (
            <React.Fragment key={idx}>
              <div className={`flex items-center gap-1.5 text-sm font-medium ${step === idx + 1 ? 'text-primary-600 dark:text-primary-400' : step > idx + 1 ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step === idx + 1 ? 'bg-primary-600 text-white' : step > idx + 1 ? 'bg-green-500 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-500'}`}>
                  {step > idx + 1 ? '✓' : idx + 1}
                </div>
                {label}
              </div>
              {idx < 2 && <ChevronRight className="w-4 h-4 text-gray-300" />}
            </React.Fragment>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">

          {/* ── STEP 1: 파일 업로드 ── */}
          {step === 1 && (
            <div className="space-y-4">
              {/* 드롭존 */}
              <div
                onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${isDragging ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-300 dark:border-gray-600 hover:border-primary-400 hover:bg-gray-50 dark:hover:bg-gray-700/30'}`}
              >
                <FileSpreadsheet className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="font-medium text-gray-700 dark:text-gray-300">파일을 드래그하거나 클릭하여 선택</p>
                <p className="text-sm text-gray-400 mt-1">.xlsx, .xls, .csv 지원</p>
                <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFileChange} className="hidden" />
              </div>

              {/* 템플릿 안내 */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 text-sm text-blue-800 dark:text-blue-300">
                <p className="font-semibold mb-1">📋 엑셀 컬럼 예시</p>
                <p className="text-xs text-blue-600 dark:text-blue-400">이름 / 전화번호 / 성별 / 생년월일 / 메모 / 회원권 상품명 / 회원권 시작일 / 회원권 만료일</p>
                <p className="text-xs mt-1 opacity-70">컬럼명이 달라도 다음 단계에서 직접 매핑할 수 있습니다.</p>
              </div>
            </div>
          )}

          {/* ── STEP 2: 컬럼 매핑 ── */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <FileSpreadsheet className="w-4 h-4" />
                <span>{fileName}</span>
                <span className="text-gray-300">·</span>
                <span>{rawRows.length}행 감지됨</span>
              </div>

              <div className="space-y-1">
                {/* 회원 기본정보 */}
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide pt-1">회원 기본 정보</p>
                {OUR_FIELDS.filter(f => f.group === 'member').map(field => (
                  <div key={field.key} className="flex items-center gap-3 py-2 border-b border-gray-100 dark:border-gray-700">
                    <div className="w-28 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
                      {field.label}
                      {field.required && <span className="text-red-500 text-xs">*</span>}
                    </div>
                    <select
                      className="input flex-1 text-sm py-1.5"
                      value={mapping[field.key] || ''}
                      onChange={e => setMapping(m => ({ ...m, [field.key]: e.target.value }))}
                    >
                      <option value="">— 매핑 안함 —</option>
                      {rawHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                    {mapping[field.key] && rawRows[0] && (
                      <span className="text-xs text-gray-400 w-24 truncate">예: {rawRows[0][mapping[field.key]] || '-'}</span>
                    )}
                  </div>
                ))}

                {/* 회원권 정보 */}
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide pt-3">회원권 정보 (선택)</p>
                {OUR_FIELDS.filter(f => f.group === 'membership').map(field => (
                  <div key={field.key} className="flex items-center gap-3 py-2 border-b border-gray-100 dark:border-gray-700">
                    <div className="w-28 text-sm font-medium text-gray-700 dark:text-gray-300">{field.label}</div>
                    <select
                      className="input flex-1 text-sm py-1.5"
                      value={mapping[field.key] || ''}
                      onChange={e => setMapping(m => ({ ...m, [field.key]: e.target.value }))}
                    >
                      <option value="">— 매핑 안함 —</option>
                      {rawHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                    {mapping[field.key] && rawRows[0] && (
                      <span className="text-xs text-gray-400 w-24 truncate">예: {rawRows[0][mapping[field.key]] || '-'}</span>
                    )}
                  </div>
                ))}
              </div>

              {/* 미리보기 (처음 3행) */}
              {transformedRows.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">데이터 미리보기 (처음 3행)</p>
                  <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-600">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          {OUR_FIELDS.filter(f => mapping[f.key]).map(f => (
                            <th key={f.key} className="px-2 py-1.5 text-left text-gray-600 dark:text-gray-300 font-medium">{f.label}</th>
                          ))}
                          <th className="px-2 py-1.5 text-left text-gray-600 dark:text-gray-300 font-medium">상태</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transformedRows.slice(0, 3).map((row, idx) => {
                          const errors = validateRow(row);
                          return (
                            <tr key={idx} className={errors.length ? 'bg-red-50 dark:bg-red-900/10' : ''}>
                              {OUR_FIELDS.filter(f => mapping[f.key]).map(f => (
                                <td key={f.key} className="px-2 py-1.5 text-gray-700 dark:text-gray-300">{row[f.key] || '-'}</td>
                              ))}
                              <td className="px-2 py-1.5">
                                {errors.length ? <span className="text-red-500">{errors[0]}</span> : <span className="text-green-600">✓</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 3: 가져오기 ── */}
          {step === 3 && (
            <div className="space-y-4">
              {!result ? (
                <>
                  {/* 요약 */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{validRows.length}</p>
                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">등록 예정 회원</p>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-green-700 dark:text-green-300">{withMembership.length}</p>
                      <p className="text-xs text-green-600 dark:text-green-400 mt-1">회원권 포함</p>
                    </div>
                    <div className={`rounded-xl p-4 text-center ${invalidRows.length > 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-gray-50 dark:bg-gray-700/30'}`}>
                      <p className={`text-2xl font-bold ${invalidRows.length > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}>{invalidRows.length}</p>
                      <p className={`text-xs mt-1 ${invalidRows.length > 0 ? 'text-red-500' : 'text-gray-400'}`}>오류 (건너뜀)</p>
                    </div>
                  </div>

                  {/* 오류 행 미리보기 */}
                  {invalidRows.length > 0 && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3">
                      <p className="text-xs font-semibold text-red-700 dark:text-red-400 mb-2">오류 행 (등록 제외)</p>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {invalidRows.slice(0, 5).map((row, idx) => (
                          <div key={idx} className="text-xs text-red-600 dark:text-red-400">
                            {row.name || '(이름없음)'} / {row.phone || '(전화없음)'} — {validateRow(row).join(', ')}
                          </div>
                        ))}
                        {invalidRows.length > 5 && <p className="text-xs text-red-400">...외 {invalidRows.length - 5}건</p>}
                      </div>
                    </div>
                  )}

                  {/* 진행 바 */}
                  {importing && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>가져오는 중...</span>
                        <span>{progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </>
              ) : (
                /* 완료 결과 */
                <div className="text-center space-y-4 py-4">
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">가져오기 완료!</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      회원 {result.memberSuccess}명 등록 · 회원권 {result.membershipSuccess}건 등록
                      {result.memberFailed > 0 && ` · 실패 ${result.memberFailed}명 (중복 전화번호 등)`}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 하단 버튼 */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between">
          <button
            onClick={step === 1 ? handleClose : () => setStep(s => s - 1)}
            disabled={importing}
            className="btn-secondary flex items-center gap-2"
          >
            {step === 1 ? '취소' : <><ChevronLeft className="w-4 h-4" />이전</>}
          </button>

          {step === 1 && (
            <button onClick={() => fileInputRef.current?.click()} className="btn-primary flex items-center gap-2">
              <Upload className="w-4 h-4" />
              파일 선택
            </button>
          )}

          {step === 2 && (
            <button
              onClick={() => setStep(3)}
              disabled={!mapping.name || !mapping.phone}
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              다음 <ChevronRight className="w-4 h-4" />
            </button>
          )}

          {step === 3 && !result && (
            <button
              onClick={handleImport}
              disabled={importing || validRows.length === 0}
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              {importing ? '가져오는 중...' : `${validRows.length}명 가져오기`}
            </button>
          )}

          {step === 3 && result && (
            <button onClick={handleClose} className="btn-primary">
              완료
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
